import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/segment/segment';
import { EventName } from '@/shared/types/segment';
import { formatDate, timeFromNow } from '@/util/dateUtils';

import { WebSession } from './api';

interface DetailRowProps {
  label: string;
  value: string;
  mono?: boolean;
}

const DetailRow = ({ label, value, mono }: DetailRowProps) => (
  <div className="flex items-start justify-between gap-4">
    <dt className="shrink-0 font-medium text-foreground">{label}</dt>
    <dd
      className={cn(
        'min-w-0 flex-1 text-right text-foreground-muted',
        mono && 'font-mono text-xs break-all'
      )}
    >
      {value || '—'}
    </dd>
  </div>
);

interface WebSessionDetailsDialogProps {
  session: WebSession | null;
  onClose: () => void;
  onRevoke: (id: string) => Promise<void>;
  isRevoking: boolean;
}

const WebSessionDetailsDialog = ({
  session,
  onClose,
  onRevoke,
  isRevoking,
}: WebSessionDetailsDialogProps) => {
  const { t } = useTranslation();

  if (!session) {
    return null;
  }

  const lastSeen =
    session.is_current || session.is_active
      ? t('web_sessions.active_now')
      : timeFromNow(session.last_seen_at);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{session.name}</DialogTitle>
          <DialogDescription>
            {session.location || t('web_sessions.unknown_location')}
          </DialogDescription>
        </DialogHeader>
        <dl className="space-y-3 py-2 text-sm">
          <DetailRow label={t('web_sessions.ip_address')} value={session.ip} mono />
          <DetailRow
            label={t('web_sessions.signed_in')}
            value={formatDate(session.created_at, 'MMM d, yyyy, h:mm a')}
          />
          <DetailRow label={t('web_sessions.last_active_label')} value={lastSeen} />
          <DetailRow label={t('web_sessions.user_agent')} value={session.user_agent} mono />
        </dl>
        <DialogFooter>
          {session.is_current ? (
            <span className="text-xs text-foreground-muted">
              {t('web_sessions.current_session_hint')}
            </span>
          ) : (
            <Button
              variant="destructive"
              disabled={isRevoking}
              onClick={async () => {
                trackEvent(EventName.ClickRevokeWebSession);
                try {
                  await onRevoke(session.id);
                  onClose();
                } catch {
                  // mutation.onError already toasted; keep dialog open for retry
                }
              }}
            >
              {t('web_sessions.revoke_access')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WebSessionDetailsDialog;
